import React from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import { IconButton } from '@atlaskit/button/new';
import RemoveIcon from '@atlaskit/icon/core/migration/delete--editor-remove';
import EditIcon from '@atlaskit/icon/core/migration/edit--editor-edit';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';
import { RuleItemGroup, RuleItemRow } from '@atlassian/eoc-og-rule';
import { useResponderNames } from '@atlassian/eoc-responder-names';
import { RuleCard } from '@atlassian/eoc-rule-card';

import { Responder } from '../responder';

import messages from './messages';
import { ActionButtons, LozengeWrapper, StyledFlex, transparentLozengeStyle } from './styled';
import { type RuleProps } from './types';

export const Rule = ({
	data,
	inlineResponder,
	onEdit,
	onDelete,
	shouldHideEditButton,
	shouldHideDeleteButton,
}: RuleProps) => {
	const { formatMessage } = useIntl();

	const [responder] = useResponderNames(
		inlineResponder ? [] : [{ id: data.notifyId, type: data.type }],
	);

	return (
		<RuleCard>
			<StyledFlex tabIndex={0} flexDirection="column" rowGap={1}>
				<RuleItemGroup
					left={
						<LozengeWrapper>
							<Lozenge appearance="inprogress">{formatMessage(messages.if)}</Lozenge>
						</LozengeWrapper>
					}
				>
					<RuleItemRow>{`${formatMessage(messages[data.notifyCondition])}, `}</RuleItemRow>
				</RuleItemGroup>
				<RuleItemGroup
					left={
						<LozengeWrapper>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
							<Lozenge style={transparentLozengeStyle}>{formatMessage(messages.then)}</Lozenge>
						</LozengeWrapper>
					}
				>
					<RuleItemRow>
						{formatMessage(messages['branchVerbalForm'], {
							time: data.time,
							type: data.type,
							notifyType: data.notifyType,
							strong: (s) => <Text as="span">{s}</Text>,
							responder: () => <Responder data={responder} inlineResponder={inlineResponder} />,
						})}
					</RuleItemRow>
				</RuleItemGroup>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<ActionButtons className="eoc-escalation-policies-common-rule-action-buttons">
					<ButtonGroup>
						{!shouldHideEditButton && (
							<IconButton
								label={formatMessage(messages.edit)}
								appearance="subtle"
								onClick={onEdit}
								icon={EditIcon}
							/>
						)}
						{!shouldHideDeleteButton && (
							<IconButton
								label={formatMessage(messages.delete)}
								onClick={() => {
									if (onDelete) {
										onDelete(data);
									}
								}}
								appearance="subtle"
								icon={RemoveIcon}
							/>
						)}
					</ButtonGroup>
				</ActionButtons>
			</StyledFlex>
		</RuleCard>
	);
};
